import { KitButton, KitLink, KitLoader, KitText } from '@omni/kit/components';
import { PixelRatio, Platform, StyleSheet, View } from 'react-native';
import React, { useEffect } from 'react';

import { AuthProvider } from '@omni/kit/services/AuthService/Types';
import AuthService from '@omni/kit/services/AuthService';
import Colors from '@omni/kit/theming/Colors';
import Environment from '@omni/kit/Environment';
import KitAvatar from '@omni/kit/components/KitAvatar';
import KitImage from '@omni/kit/components/KitImage';
import { ParamListBase } from '@react-navigation/native';
import { SafeAreaView } from 'react-native-safe-area-context';
import Spacing from '@omni/kit/theming/Spacing';
import { StackNavigationProp } from '@react-navigation/stack';
import { WindowSizeMin } from '@omni/kit/contexts/SizeClassContext';
import appendUrlParams from '@omni/kit/utilities/appendUrlParams';
import { buildAppIconUrl } from '@omni/kit/hooks/useFavicon';
import useAuthProviders from '@omni/kit/hooks/useAuthProviders';
import { useSourceContext } from '@omni/kit/contexts/SourceContext';
import { useTranslation } from 'react-i18next';

const debug = require('debug')('tca:auth:AuthProviderList');

/**
 * These are the dimensions of the new browser window
 * which opens to show the third party login page
 *
 * The popup's width has to be at least medium in size to
 * not break messaging web because it actually renders
 * the native messaging when window size is small which will
 * prevent the redirect route from being hit
 */
const [POPUP_WIDTH, POPUP_HEIGHT] = [
  WindowSizeMin.Medium,
  WindowSizeMin.Medium,
];

interface AuthProviderListProps {
  appKey: string;
  customReturnUrl?: string;
  customHeader?: JSX.Element;
  navigation?: StackNavigationProp<ParamListBase>;
  openInNewTab?: boolean;
  orgKey?: string;
  targetAuthProviderId: string;
}

export default function AuthProviderList({
  appKey,
  customReturnUrl,
  customHeader,
  openInNewTab,
  orgKey,
  targetAuthProviderId,
}: AuthProviderListProps): JSX.Element {
  const source = useSourceContext();

  const { isFetching, loginEnabledProviders, providers } = useAuthProviders({
    appKey,
    orgKey,
  });

  // If there's only one valid login option, auto-select it immediately (e.g. Ligonier)
  useEffect(() => {
    if (!isFetching && loginEnabledProviders.length === 1) {
      _handleAuthProviderSelected(loginEnabledProviders[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching, loginEnabledProviders]);

  const _handleAuthProviderSelected = (provider: AuthProvider) => {
    const params = [];
    params.push(`branding_app_key=${appKey}`);
    if (source) params.push(`source=${source}`);

    const authUrl = appendUrlParams(provider.auth_url, params);

    const targetProvider = providers.find(
      (c) => c.authproviderid === targetAuthProviderId
    );

    debug(
      `target:${targetProvider?.authproviderid}, selected:${provider.authproviderid}`
    );

    /**
     * The target auth provider is usually the Subsplash auth provider context,
     * except in enterprise apps, like Ligonier (JF2Q5K).
     * ref: https://subsplash.atlassian.net/wiki/spaces/BKND/pages/208797699/End+User+Login+Workflow#Target-Provider
     */
    if (!targetProvider) {
      return;
    }

    const refreshTokenEndpoint = targetProvider?.token_url || '';

    AuthService.refreshTokenEndpoint = refreshTokenEndpoint;

    if (Platform.OS === 'web') {
      const returnUrl = encodeURIComponent(
        customReturnUrl || `${Environment.host}/${appKey}/auth`
      );

      let url = `${authUrl}&return_url=${returnUrl}&skip_deep_link=true&use_refresh_cookie=true`;

      /**
       * Auto redirect with fake token for localhost
       */
      if (window.location.hostname === 'localhost') {
        if (customReturnUrl) {
          url = customReturnUrl;
        } else {
          url = `${window.location.origin}/${appKey}/auth`;
        }

        url += `?access_token=${Environment.returnUrlToken}&token_type=bearer`;
      }

      // @ts-ignore
      window.reloadAuth = ({
        accessToken,
        refreshToken,
      }: {
        accessToken?: string;
        refreshToken?: string;
      }) => {
        debug('reloadAuth');
        if (accessToken) {
          AuthService.accessToken = accessToken;
        }

        if (refreshToken) {
          AuthService.refreshToken = refreshToken;
        }

        AuthService.loadSavedCredentials();
      };

      if (openInNewTab) {
        window.open(
          url,
          'subsplash_auth',
          `location=yes,height=${POPUP_HEIGHT},width=${POPUP_WIDTH},top=${
            window.screen.height / 2 - POPUP_HEIGHT / 2
          },left=${
            window.screen.width / 2 - POPUP_WIDTH / 2
          },scrollbars=yes,status=yes`
        );
      } else {
        // @ts-ignore
        window.location = new URL(url, window.location).toString();
      }
    } else {
      AuthService.loginFromNativeBridge(targetProvider, provider, source);
    }
  };

  return (
    <SafeAreaView
      style={{
        alignItems: 'center',
        backgroundColor: Colors.N0,
        justifyContent: 'center',
        flexGrow: 1, // needed to fill the remaining space
      }}
    >
      {isFetching ? (
        <View style={styles.loadingStyle}>
          <KitLoader />
        </View>
      ) : (
        <View style={styles.container}>
          <View style={styles.authProviderContainer}>
            {customHeader || <LoginHeader appKey={appKey} />}
            <View style={styles.authProvider}>
              {loginEnabledProviders.map((provider) => (
                <View
                  key={provider.authproviderid}
                  style={{ marginBottom: Spacing.m }}
                >
                  <KitButton
                    icon={provider.icon}
                    titleStyle={{ marginLeft: 10 }}
                    onPress={() => _handleAuthProviderSelected(provider)}
                    title={`Continue with ${provider.title}`}
                    color={
                      provider.service_name === 'apple'
                        ? Colors.N1000
                        : undefined
                    }
                    secondary={provider.service_name !== 'apple'}
                  />
                </View>
              ))}
              <KitText
                center
                fontSize={14}
                style={{ marginTop: 6, paddingHorizontal: Spacing.xs }}
              >
                By continuing you agree to Subsplash's{' '}
                <KitLink
                  href='https://www.subsplash.com/legal/terms-of-use'
                  fontSize={14}
                >
                  Terms of Use
                </KitLink>{' '}
                and{' '}
                <KitLink
                  href='https://www.subsplash.com/legal/privacy'
                  fontSize={14}
                >
                  Privacy Policy
                </KitLink>
                .
              </KitText>
            </View>
          </View>
          <View style={styles.brandingContainer}>
            <KitImage
              resizeMode='contain'
              style={{
                height: 26,
                marginTop: Platform.OS === 'web' ? Spacing.xxl : 0,
                width: 150,
              }}
              source={require('@omni/kit/img/subsplash-logo.png')}
            />
            <KitText color={Colors.N500} fontSize={14}>
              {`©${new Date().getFullYear()} Subsplash, Inc.`}
            </KitText>
          </View>
        </View>
      )}
    </SafeAreaView>
  );
}

const LoginHeader = ({ appKey }: { appKey: string }) => {
  const { t } = useTranslation(['auth']);

  return (
    <>
      <KitAvatar
        imageUrl={buildAppIconUrl(
          appKey,
          PixelRatio.getPixelSizeForLayoutSize(64)
        )}
        nickname={appKey}
        size={64}
      />
      <KitText center h1 style={{ marginTop: Spacing.m }}>
        {t('auth:loginHeaderTitle')}
      </KitText>
    </>
  );
};

const styles = StyleSheet.create({
  appleWarningBox: {
    alignItems: 'center',
    backgroundColor: Colors.N50,
    borderRadius: 6,
    flexDirection: 'row',
    marginTop: Spacing.xxl,
    maxWidth: 423,
    padding: Spacing.l,
  },
  authProvider: {
    marginTop: Spacing.xl,
    width: '100%',
  },
  authProviderContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    marginBottom: Spacing.xxl,
    paddingHorizontal: Spacing.s,
    maxWidth: 350,
    width: '100%',
  },
  container: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    maxWidth: 455,
    width: '100%',
  },
  brandingContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  },
  loadingStyle: {
    alignItems: 'center',
    minHeight: 200,
    justifyContent: 'center',
  },
});
